import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { Cloud, Download, LogOut, Monitor } from 'lucide-react'
import type { PortalLicenseSummary } from '@pusula/shared'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { ConfirmModal, type ConfirmModalData } from '@/components/ui/ConfirmModal'
import { Section, apiError, formatBytes, formatDateTime } from '@/pages/settings/datacenter/shared'
import { portalApi, setPortalToken, type DesktopRelease, type PortalActivation, type PortalMe } from './portal-api'
import { MigrationNotice } from './MigrationNotice'
import { PortalAiCredits } from './PortalAiCredits'

const STATUS_STYLE: Record<PortalLicenseSummary['status'], string> = {
  ACTIVE: 'bg-success/15 text-success',
  SUSPENDED: 'bg-warning/15 text-warning',
  REVOKED: 'bg-destructive/15 text-destructive',
}

function LicenseCard({ license }: { license: PortalLicenseSummary }): JSX.Element {
  const { t, i18n } = useTranslation('portal')
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState<ConfirmModalData | null>(null)
  const { data: activations } = useQuery({
    queryKey: ['portal-activations', license.id],
    queryFn: () => portalApi.get<{ data: PortalActivation[] }>(`/portal/licenses/${license.id}/activations`).then((r) => r.data.data),
  })
  const unbind = useMutation({
    mutationFn: (id: string) => portalApi.post(`/portal/activations/${id}/deactivate`),
    onSuccess: () => { toast.success(t('home.unbound')); void qc.invalidateQueries({ queryKey: ['portal-activations', license.id] }) },
    onError: (e) => toast.error(apiError(e)),
  })
  const daysLeft = Math.ceil((Date.parse(license.validUntil) - Date.now()) / 86400_000)

  return (
    <div className="rounded-lg border border-border/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-foreground">{license.companyName}</p>
          <p className="text-xs text-muted-foreground">{license.keyPrefix}… · {license.plan} · {t('home.users', { count: license.userLimit })}</p>
        </div>
        <span className={`badge ${STATUS_STYLE[license.status]}`}>{t(`home.statuses.${license.status}`)}</span>
      </div>
      <p className={`mt-2 text-sm ${daysLeft < 30 ? 'text-warning' : 'text-muted-foreground'}`}>
        {t('home.validUntil', { date: formatDateTime(license.validUntil, i18n.language), days: Math.max(0, daysLeft) })}
      </p>
      <p className="mt-3 text-xs font-semibold uppercase text-muted-foreground">{t('home.installs', { used: license.activeActivations, max: license.maxActivations })}</p>
      <div className="mt-1 divide-y divide-border/30">
        {(activations ?? []).filter((a) => a.status === 'ACTIVE').map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
            <span className="flex items-center gap-2 text-foreground"><Monitor className="h-4 w-4 text-muted-foreground" />{a.hostname ?? '?'}</span>
            <span className="text-xs text-muted-foreground">{a.appVersion ?? ''} · {t('home.lastSeen', { date: formatDateTime(a.lastHeartbeatAt, i18n.language) })}</span>
            <button type="button" className="btn-ghost text-xs text-destructive" onClick={() => setConfirm({
              title: t('home.unbindTitle'), message: t('home.unbindMessage', { host: a.hostname ?? '' }), danger: true, confirmLabel: t('home.unbind'),
              onConfirm: () => unbind.mutate(a.id),
            })}>{t('home.unbind')}</button>
          </div>
        ))}
        {activations && !activations.some((a) => a.status === 'ACTIVE') && <p className="py-2 text-sm text-muted-foreground">{t('home.noInstalls')}</p>}
      </div>
      <ConfirmModal data={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}

function Downloads(): JSX.Element {
  const { t, i18n } = useTranslation('portal')
  const { data } = useQuery({
    queryKey: ['portal-downloads'],
    queryFn: () => portalApi.get<{ data: DesktopRelease[] }>('/portal/downloads').then((r) => r.data.data),
  })
  const latest = data?.find((r) => !r.prerelease && r.installer)
  return (
    <Section title={t('downloads.title')} description={t('downloads.description')}>
      {!latest ? <p className="text-sm text-muted-foreground">{t('downloads.none')}</p> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium text-foreground">Pusula {latest.version}</p>
              <p className="text-xs text-muted-foreground">{formatDateTime(latest.publishedAt, i18n.language)} · {latest.installer ? formatBytes(latest.installer.sizeBytes) : ''}</p>
            </div>
            {latest.installer && (
              <a className="btn-primary flex items-center gap-2" href={latest.installer.url}><Download className="h-4 w-4" />{t('downloads.download')}</a>
            )}
          </div>
          {latest.notes && <pre className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-foreground">{latest.notes}</pre>}
          <p className="text-xs text-muted-foreground">{t('downloads.requirements')}</p>
          {data && data.length > 1 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">{t('downloads.older')}</summary>
              {data.filter((r) => r !== latest).map((r) => (
                <p key={r.version} className="py-1">
                  {r.installer ? <a className="text-primary hover:underline" href={r.installer.url}>{r.version}</a> : r.version}
                  <span className="ml-2 text-xs text-muted-foreground">{formatDateTime(r.publishedAt, i18n.language)}</span>
                </p>
              ))}
            </details>
          )}
        </div>
      )}
    </Section>
  )
}

/** Barındırılan sürüm: masaüstüne seçenek olarak sunulur (adres derleme anında verilir) */
function CloudOption(): JSX.Element | null {
  const { t } = useTranslation('portal')
  const url = import.meta.env.VITE_CLOUD_APP_URL?.replace(/\/$/, '')
  if (!url) return null
  return (
    <Section title={t('cloud.title')} description={t('cloud.description')}>
      <div className="flex flex-wrap items-center gap-3">
        <a className="btn-primary flex items-center gap-2" href={`${url}/login`}><Cloud className="h-4 w-4" />{t('cloud.login')}</a>
        <a className="btn-secondary" href={`${url}/register`}>{t('cloud.register')}</a>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{t('cloud.note')}</p>
    </Section>
  )
}

function Account(): JSX.Element {
  const { t } = useTranslation('portal')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const change = useMutation({
    mutationFn: () => portalApi.post('/portal/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => { toast.success(t('account.changed')); setCurrent(''); setNext('') },
    onError: (e) => toast.error(apiError(e)),
  })
  return (
    <Section title={t('account.title')}>
      <div className="grid gap-3 sm:grid-cols-3">
        <PasswordInput placeholder={t('account.current')} value={current} onChange={(e) => setCurrent(e.target.value)} />
        <PasswordInput placeholder={t('account.next')} value={next} onChange={(e) => setNext(e.target.value)} />
        <button type="button" className="btn-secondary" disabled={!current || !next || change.isPending} onClick={() => change.mutate()}>{t('account.change')}</button>
      </div>
    </Section>
  )
}

export function PortalHomePage(): JSX.Element {
  const { t } = useTranslation('portal')
  const navigate = useNavigate()
  const { data } = useQuery({
    queryKey: ['portal-me'],
    queryFn: () => portalApi.get<{ data: PortalMe }>('/portal/me').then((r) => r.data.data),
  })
  const logout = async (): Promise<void> => {
    await portalApi.post('/portal/auth/logout').catch(() => undefined)
    setPortalToken(null)
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Pusula Portal</h1>
            {data && <p className="text-xs text-muted-foreground">{data.user.firstName} {data.user.lastName} · {data.user.email}</p>}
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button type="button" className="btn-ghost flex items-center gap-1 text-sm" onClick={() => void logout()}><LogOut className="h-4 w-4" />{t('home.logout')}</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 p-6">
        {data && data.legacyCompanies.length > 0 && <MigrationNotice companies={data.legacyCompanies} hasLicense={data.licenses.length > 0} />}
        <Section title={t('home.licensesTitle')} description={t('home.licensesDescription')}>
          {!data ? <p className="text-sm text-muted-foreground">…</p>
            : data.licenses.length === 0 ? <p className="text-sm text-muted-foreground">{t('home.noLicenses')}</p>
              : <div className="space-y-4">{data.licenses.map((l) => <LicenseCard key={l.id} license={l} />)}</div>}
        </Section>
        <PortalAiCredits />
        <Downloads />
        <CloudOption />
        <Account />
      </main>
    </div>
  )
}
